import type {
  CoinInsertionDropAcceptedEvent,
  CoinInsertionIntent,
} from '../insertion';
import type { MedalOutcome } from '../physics';
import type {
  BonusCardRewardAwardedEvent,
  BonusCardRewardEvent,
} from '../rewards/bonus/types';
import type {
  JackpotContributionInput,
  JackpotContributionSource,
  ProgressionInsertionDecision,
  ProgressionMutationReason,
  ProgressionMutationResult,
  ResolvePendingDropInput,
  SessionProgressionConfiguration,
  SessionProgressionEvent,
  SessionProgressionEventListener,
  SessionProgressionEventPayload,
  SessionProgressionOptions,
  SessionProgressionRestartOptions,
  SessionProgressionSnapshot,
  SessionProgressionStateListener,
  SessionProgressionStatus,
  SessionProgressionUnsubscribe,
} from './types';

export const DEFAULT_INITIAL_COINS = 100;
export const DEFAULT_FRONT_COIN_WIN_AWARD = 1;
export const DEFAULT_COIN_JACKPOT_CONTRIBUTION = 1;
export const DEFAULT_JACKPOT_THRESHOLD = 100;
export const DEFAULT_JACKPOT_AWARD = 100;
export const DEFAULT_RUN_OUT_GRACE_SECONDS = 8;

const EMPTY_EVENTS: readonly SessionProgressionEvent[] = Object.freeze([]);

/**
 * Pure session economy and jackpot state. Rendering, UI, sound, physics, and
 * timers remain host-owned; run-out advances only through explicit update().
 */
export class SessionProgressionSystem {
  private readonly configurationValue: SessionProgressionConfiguration;
  private readonly eventListeners = new Set<SessionProgressionEventListener>();
  private readonly stateListeners = new Set<SessionProgressionStateListener>();
  private readonly onError: SessionProgressionOptions['onError'];
  private readonly processedInputs = new Set<string>();
  private readonly pendingDropIds = new Set<string>();

  private sessionValue = 1;
  private eventSequenceValue = 0;
  private statusValue: SessionProgressionStatus = 'active';
  private sessionInitialCoinsValue: number;
  private balanceValue: number;
  private insertionsAcceptedValue = 0;
  private coinsSpentValue = 0;
  private coinsWonValue = 0;
  private sessionScoreValue = 0;
  private frontCoinWinsValue = 0;
  private frontCoinWinCoinsValue = 0;
  private bonusAwardsValue = 0;
  private bonusCoinsValue = 0;
  private jackpotCoinsValue = 0;
  private jackpotProgressValue = 0;
  private jackpotTotalContributedValue = 0;
  private jackpotsHitValue = 0;
  private jackpotCoinsAwardedValue = 0;
  private duplicateInputsRejectedValue = 0;
  private runOutDeadlineSecondsValue: number | null = null;
  private lastActivityAtSecondsValue = 0;

  /** Adapter for CoinInsertionSystem.requestCoin. requestedAt is converted from ms to seconds. */
  readonly requestCoin = (intent: CoinInsertionIntent): ProgressionInsertionDecision => {
    const result = this.consumeInsertionIntent(intent);
    const spent = result.events.find((event) => event.type === 'insertion-spent');
    return Object.freeze(
      spent?.type === 'insertion-spent'
        ? {
            accepted: true,
            balance: result.snapshot.balance,
            dropId: spent.dropId,
          }
        : {
            accepted: false,
            reason: result.reason === 'duplicate-input' ? 'duplicate-input' : 'no-coins',
            balance: result.snapshot.balance,
          },
    );
  };

  /** Adapter for MedalPhysicsSystem.onOutcome. */
  readonly onMedalOutcome = (outcome: MedalOutcome): void => {
    this.consumeMedalOutcome(outcome);
  };

  /** Adapter for BonusCardRewardSystem reward events. Non-reward events are ignored safely. */
  readonly onBonusEvent = (event: BonusCardRewardEvent): void => {
    this.consumeBonusEvent(event);
  };

  constructor(options: SessionProgressionOptions = {}) {
    this.configurationValue = normalizeConfiguration(options);
    this.sessionInitialCoinsValue = this.configurationValue.initialCoins;
    this.balanceValue = this.configurationValue.initialCoins;
    this.onError = options.onError;
    if (options.onEvent) this.eventListeners.add(options.onEvent);
    if (options.onStateChange) this.stateListeners.add(options.onStateChange);
  }

  get configuration(): SessionProgressionConfiguration {
    return this.configurationValue;
  }

  get snapshot(): SessionProgressionSnapshot {
    return this.getSnapshot();
  }

  getSnapshot(): SessionProgressionSnapshot {
    const pendingIds = Object.freeze([...this.pendingDropIds]);
    return Object.freeze({
      schemaVersion: 1 as const,
      session: this.sessionValue,
      status: this.statusValue,
      balance: this.balanceValue,
      initialCoins: this.sessionInitialCoinsValue,
      insertionCost: 1 as const,
      insertionsAccepted: this.insertionsAcceptedValue,
      coinsSpent: this.coinsSpentValue,
      coinsWon: this.coinsWonValue,
      sessionScore: this.sessionScoreValue,
      frontCoinWins: this.frontCoinWinsValue,
      frontCoinWinCoins: this.frontCoinWinCoinsValue,
      bonusAwards: this.bonusAwardsValue,
      bonusCoins: this.bonusCoinsValue,
      jackpotCoins: this.jackpotCoinsValue,
      jackpot: Object.freeze({
        threshold: this.configurationValue.jackpotThreshold,
        awardCoins: this.configurationValue.jackpotAward,
        overflowPolicy: this.configurationValue.jackpotOverflowPolicy,
        progress: this.jackpotProgressValue,
        totalContributed: this.jackpotTotalContributedValue,
        jackpotsHit: this.jackpotsHitValue,
        coinsAwarded: this.jackpotCoinsAwardedValue,
      }),
      pendingDropCount: pendingIds.length,
      pendingDropIds: pendingIds,
      runOutDeadlineSeconds: this.runOutDeadlineSecondsValue,
      processedInputCount: this.processedInputs.size,
      duplicateInputsRejected: this.duplicateInputsRejectedValue,
      eventSequence: this.eventSequenceValue,
      lastActivityAtSeconds: this.lastActivityAtSecondsValue,
    });
  }

  onEvent(listener: SessionProgressionEventListener): SessionProgressionUnsubscribe {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  onStateChange(listener: SessionProgressionStateListener): SessionProgressionUnsubscribe {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  createDropId(insertionId: string | number): string {
    const id = normalizeId(String(insertionId), 'insertionId');
    return `progression:s${this.sessionValue}:drop:${encodeURIComponent(id)}`;
  }

  consumeAcceptedInsertion(
    input: CoinInsertionIntent | CoinInsertionDropAcceptedEvent,
  ): ProgressionMutationResult {
    return this.consumeInsertionIntent('intent' in input ? input.intent : input);
  }

  consumeInsertionIntent(intent: CoinInsertionIntent): ProgressionMutationResult {
    if (intent.cost !== 1) throw new Error('Session progression requires insertion cost to equal one coin.');
    const insertionId = String(intent.id);
    const atSeconds = normalizeSeconds(intent.requestedAt * 0.001, 'intent.requestedAt / 1000');
    this.observeTime(atSeconds);
    const processedKey = `insertion:${insertionId}`;
    const events: SessionProgressionEvent[] = [];

    if (this.processedInputs.has(processedKey)) {
      this.recordDuplicate(events, 'insertion', insertionId, atSeconds);
      this.pushEvent(events, {
        type: 'insertion-rejected',
        atSeconds,
        insertionId,
        reason: 'duplicate-input',
        balance: this.balanceValue,
      });
      return this.finish('rejected', 'duplicate-input', events, true);
    }
    this.processedInputs.add(processedKey);

    if (this.balanceValue < 1) {
      this.pushEvent(events, {
        type: 'insertion-rejected',
        atSeconds,
        insertionId,
        reason: 'no-coins',
        balance: this.balanceValue,
      });
      return this.finish('rejected', 'no-coins', events, true);
    }

    const balanceBefore = this.balanceValue;
    this.balanceValue -= 1;
    this.insertionsAcceptedValue = safeIncrement(this.insertionsAcceptedValue, 'insertionsAccepted');
    this.coinsSpentValue = safeIncrement(this.coinsSpentValue, 'coinsSpent');
    const dropId = this.createDropId(insertionId);
    this.pendingDropIds.add(dropId);
    this.pushEvent(events, {
      type: 'insertion-spent',
      atSeconds,
      insertionId,
      dropId,
      cost: 1,
      balanceBefore,
      balanceAfter: this.balanceValue,
    });
    this.scheduleRunOutIfNeeded(atSeconds, events);
    return this.finish('applied', null, events, true);
  }

  consumeMedalOutcome(outcome: MedalOutcome, atSeconds = outcome.simulatedSeconds): ProgressionMutationResult {
    const time = normalizeSeconds(atSeconds, 'atSeconds');
    this.observeTime(time);
    const itemId = normalizeId(outcome.id, 'outcome.id');
    const outcomeId = `${outcome.itemKind}:${itemId}`;
    const processedKey = `medal:${outcomeId}`;
    const events: SessionProgressionEvent[] = [];
    if (this.processedInputs.has(processedKey)) {
      this.recordDuplicate(events, 'medal-outcome', outcomeId, time);
      return this.finish('rejected', 'duplicate-input', events, true);
    }
    this.processedInputs.add(processedKey);

    const resolvedPending = this.resolvePendingInternal(
      itemId,
      time,
      events,
      outcome.result,
    );
    if (outcome.itemKind === 'card') {
      return this.finish('ignored', 'card-outcome', events, true);
    }
    if (outcome.result !== 'front-win') {
      return this.finish(resolvedPending ? 'applied' : 'ignored', 'coin-side-loss', events, true);
    }

    this.frontCoinWinsValue = safeIncrement(this.frontCoinWinsValue, 'frontCoinWins');
    this.creditCoins(this.configurationValue.frontCoinWinAward, 'front-win', time, events);
    this.pushEvent(events, {
      type: 'coin-won',
      atSeconds: time,
      outcomeId,
      itemId,
      coins: this.configurationValue.frontCoinWinAward,
      jackpotContribution: this.configurationValue.coinJackpotContribution,
      balanceAfter: this.balanceValue,
    });
    this.applyJackpotContribution(
      `coin:${outcomeId}`,
      'coin',
      this.configurationValue.coinJackpotContribution,
      time,
      events,
    );
    return this.finish('applied', null, events, true);
  }

  consumeBonusEvent(event: BonusCardRewardEvent, atSeconds = event.simulatedSeconds): ProgressionMutationResult {
    if (event.type !== 'reward') {
      return this.finish('ignored', 'not-a-reward-event', [], false);
    }
    return this.consumeBonusReward(event, atSeconds);
  }

  consumeBonusReward(
    event: BonusCardRewardAwardedEvent,
    atSeconds = event.simulatedSeconds,
  ): ProgressionMutationResult {
    const time = normalizeSeconds(atSeconds, 'atSeconds');
    this.observeTime(time);
    const outcomeId = normalizeId(event.outcomeId, 'event.outcomeId');
    const processedKey = `bonus:${outcomeId}`;
    const events: SessionProgressionEvent[] = [];
    if (this.processedInputs.has(processedKey)) {
      this.recordDuplicate(events, 'bonus-reward', outcomeId, time);
      return this.finish('rejected', 'duplicate-input', events, true);
    }
    this.processedInputs.add(processedKey);
    const coins = normalizeNonNegativeInteger(event.extraCoins, 'event.extraCoins');
    const contribution = normalizeNonNegativeInteger(
      event.recommendedJackpotContribution,
      'event.recommendedJackpotContribution',
    );
    this.bonusAwardsValue = safeIncrement(this.bonusAwardsValue, 'bonusAwards');
    this.creditCoins(coins, 'bonus', time, events);
    this.pushEvent(events, {
      type: 'bonus-awarded',
      atSeconds: time,
      outcomeId,
      bonusSequence: event.sequence,
      coins,
      jackpotContribution: contribution,
      balanceAfter: this.balanceValue,
    });
    this.applyJackpotContribution(`card:${outcomeId}`, 'card', contribution, time, events);
    return this.finish('applied', null, events, true);
  }

  contributeToJackpot(input: JackpotContributionInput): ProgressionMutationResult {
    const id = normalizeId(input.id, 'input.id');
    const time = normalizeSeconds(input.atSeconds, 'input.atSeconds');
    const source = input.source ?? 'manual';
    const amount = normalizeNonNegativeInteger(input.amount, 'input.amount');
    this.observeTime(time);
    const processedKey = `jackpot:${source}:${id}`;
    const events: SessionProgressionEvent[] = [];
    if (this.processedInputs.has(processedKey)) {
      this.recordDuplicate(events, 'jackpot-contribution', id, time);
      return this.finish('rejected', 'duplicate-input', events, true);
    }
    this.processedInputs.add(processedKey);
    this.applyJackpotContribution(id, source, amount, time, events);
    return this.finish('applied', null, events, true);
  }

  resolvePendingDrop(input: ResolvePendingDropInput): ProgressionMutationResult {
    const id = normalizeId(input.dropId, 'input.dropId');
    const time = normalizeSeconds(input.atSeconds, 'input.atSeconds');
    this.observeTime(time);
    const events: SessionProgressionEvent[] = [];
    const resolved = this.resolvePendingInternal(id, time, events, 'manual');
    return this.finish(resolved ? 'applied' : 'ignored', resolved ? null : 'no-state-change', events, resolved);
  }

  /** Advances only deadline-based state. No wall-clock timer is installed. */
  update(nowSeconds: number): ProgressionMutationResult {
    const time = normalizeSeconds(nowSeconds, 'nowSeconds');
    this.observeTime(time);
    const deadline = this.runOutDeadlineSecondsValue;
    if (this.statusValue !== 'run-out-pending' || deadline === null || time < deadline) {
      return this.finish('ignored', 'no-state-change', [], false);
    }
    const events: SessionProgressionEvent[] = [];
    this.statusValue = 'run-out';
    this.pushEvent(events, {
      type: 'session-run-out',
      atSeconds: time,
      deadlineSeconds: deadline,
      pendingDropCount: this.pendingDropIds.size,
    });
    return this.finish('applied', null, events, true);
  }

  restart(options: SessionProgressionRestartOptions = {}): ProgressionMutationResult {
    const atSeconds = normalizeSeconds(
      options.atSeconds ?? this.lastActivityAtSecondsValue,
      'options.atSeconds',
    );
    const coins = normalizeNonNegativeInteger(
      options.coins ?? this.configurationValue.initialCoins,
      'options.coins',
    );
    const progressPolicy = options.jackpotProgress ?? 'reset';
    const carriedProgress = progressPolicy === 'carry' ? this.jackpotProgressValue : 0;
    const previousSession = this.sessionValue;

    this.sessionValue = safeIncrement(this.sessionValue, 'session');
    this.eventSequenceValue = 0;
    this.statusValue = 'active';
    this.sessionInitialCoinsValue = coins;
    this.balanceValue = coins;
    this.insertionsAcceptedValue = 0;
    this.coinsSpentValue = 0;
    this.coinsWonValue = 0;
    this.sessionScoreValue = 0;
    this.frontCoinWinsValue = 0;
    this.frontCoinWinCoinsValue = 0;
    this.bonusAwardsValue = 0;
    this.bonusCoinsValue = 0;
    this.jackpotCoinsValue = 0;
    this.jackpotProgressValue = carriedProgress;
    this.jackpotTotalContributedValue = 0;
    this.jackpotsHitValue = 0;
    this.jackpotCoinsAwardedValue = 0;
    this.duplicateInputsRejectedValue = 0;
    this.runOutDeadlineSecondsValue = null;
    this.lastActivityAtSecondsValue = atSeconds;
    this.processedInputs.clear();
    this.pendingDropIds.clear();

    const events: SessionProgressionEvent[] = [];
    this.pushEvent(events, {
      type: 'session-restarted',
      atSeconds,
      previousSession,
      balance: this.balanceValue,
      jackpotProgressPolicy: progressPolicy,
      jackpotProgress: this.jackpotProgressValue,
    });
    this.scheduleRunOutIfNeeded(atSeconds, events);
    return this.finish('applied', null, events, true);
  }

  dispose(): void {
    this.eventListeners.clear();
    this.stateListeners.clear();
  }

  private resolvePendingInternal(
    dropId: string,
    atSeconds: number,
    events: SessionProgressionEvent[],
    outcome: 'front-win' | 'side-loss' | 'manual',
  ): boolean {
    if (!this.pendingDropIds.delete(dropId)) return false;
    this.pushEvent(events, {
      type: 'pending-drop-resolved',
      atSeconds,
      dropId,
      outcome,
      pendingDropCount: this.pendingDropIds.size,
    });
    return true;
  }

  private scheduleRunOutIfNeeded(
    atSeconds: number,
    events: SessionProgressionEvent[],
  ): void {
    if (this.balanceValue !== 0 || this.statusValue === 'run-out') return;
    const deadline = atSeconds + this.configurationValue.runOutGraceSeconds;
    this.statusValue = 'run-out-pending';
    this.runOutDeadlineSecondsValue = deadline;
    this.pushEvent(events, {
      type: 'run-out-pending',
      atSeconds,
      deadlineSeconds: deadline,
      graceSeconds: this.configurationValue.runOutGraceSeconds,
      pendingDropCount: this.pendingDropIds.size,
    });
  }

  private creditCoins(
    amount: number,
    source: 'front-win' | 'bonus' | 'jackpot',
    atSeconds: number,
    events: SessionProgressionEvent[],
  ): void {
    if (amount === 0) return;
    const previousStatus = this.statusValue;
    this.balanceValue = safeAdd(this.balanceValue, amount, 'balance');
    this.coinsWonValue = safeAdd(this.coinsWonValue, amount, 'coinsWon');
    this.sessionScoreValue = safeAdd(this.sessionScoreValue, amount, 'sessionScore');
    if (source === 'front-win') {
      this.frontCoinWinCoinsValue = safeAdd(this.frontCoinWinCoinsValue, amount, 'frontCoinWinCoins');
    } else if (source === 'bonus') {
      this.bonusCoinsValue = safeAdd(this.bonusCoinsValue, amount, 'bonusCoins');
    } else {
      this.jackpotCoinsValue = safeAdd(this.jackpotCoinsValue, amount, 'jackpotCoins');
      this.jackpotCoinsAwardedValue = safeAdd(
        this.jackpotCoinsAwardedValue,
        amount,
        'jackpotCoinsAwarded',
      );
    }
    if (previousStatus !== 'active') {
      this.statusValue = 'active';
      this.runOutDeadlineSecondsValue = null;
      this.pushEvent(events, {
        type: 'run-out-cancelled',
        atSeconds,
        previousStatus,
        reason: 'coins-awarded',
        balance: this.balanceValue,
      });
    }
  }

  private applyJackpotContribution(
    contributionId: string,
    source: JackpotContributionSource,
    amount: number,
    atSeconds: number,
    events: SessionProgressionEvent[],
  ): void {
    const progressBefore = this.jackpotProgressValue;
    const combined = safeAdd(progressBefore, amount, 'jackpot.progress');
    const threshold = this.configurationValue.jackpotThreshold;
    let jackpotsTriggered = 0;
    let progressAfter = combined;
    if (combined >= threshold) {
      if (this.configurationValue.jackpotOverflowPolicy === 'carry') {
        jackpotsTriggered = Math.floor(combined / threshold);
        progressAfter = combined % threshold;
      } else {
        jackpotsTriggered = 1;
        progressAfter = 0;
      }
    }
    this.jackpotProgressValue = progressAfter;
    this.jackpotTotalContributedValue = safeAdd(
      this.jackpotTotalContributedValue,
      amount,
      'jackpot.totalContributed',
    );
    this.pushEvent(events, {
      type: 'jackpot-contributed',
      atSeconds,
      contributionId,
      source,
      amount,
      progressBefore,
      progressAfter,
      jackpotsTriggered,
    });

    for (let index = 0; index < jackpotsTriggered; index += 1) {
      this.jackpotsHitValue = safeIncrement(this.jackpotsHitValue, 'jackpotsHit');
      this.creditCoins(this.configurationValue.jackpotAward, 'jackpot', atSeconds, events);
      this.pushEvent(events, {
        type: 'jackpot-hit',
        atSeconds,
        contributionId,
        source,
        jackpotNumber: this.jackpotsHitValue,
        awardCoins: this.configurationValue.jackpotAward,
        balanceAfter: this.balanceValue,
        progressAfter,
      });
    }
  }

  private recordDuplicate(
    events: SessionProgressionEvent[],
    inputKind: 'insertion' | 'medal-outcome' | 'bonus-reward' | 'jackpot-contribution',
    inputId: string,
    atSeconds: number,
  ): void {
    this.duplicateInputsRejectedValue = safeIncrement(
      this.duplicateInputsRejectedValue,
      'duplicateInputsRejected',
    );
    this.pushEvent(events, {
      type: 'duplicate-rejected',
      atSeconds,
      inputKind,
      inputId,
    });
  }

  private pushEvent(
    events: SessionProgressionEvent[],
    payload: SessionProgressionEventPayload,
  ): void {
    this.eventSequenceValue = safeIncrement(this.eventSequenceValue, 'eventSequence');
    const event = Object.freeze({
      ...payload,
      sequence: this.eventSequenceValue,
      session: this.sessionValue,
    }) as SessionProgressionEvent;
    events.push(event);
  }

  private observeTime(atSeconds: number): void {
    this.lastActivityAtSecondsValue = Math.max(this.lastActivityAtSecondsValue, atSeconds);
  }

  private finish(
    status: ProgressionMutationResult['status'],
    reason: ProgressionMutationReason | null,
    events: SessionProgressionEvent[],
    notify: boolean,
  ): ProgressionMutationResult {
    const immutableEvents = events.length === 0 ? EMPTY_EVENTS : Object.freeze([...events]);
    const snapshot = this.getSnapshot();
    for (const event of immutableEvents) this.emitEvent(event);
    if (notify) this.emitState(snapshot);
    return Object.freeze({ status, reason, events: immutableEvents, snapshot });
  }

  private emitEvent(event: SessionProgressionEvent): void {
    for (const listener of [...this.eventListeners]) {
      try {
        listener(event);
      } catch (error) {
        this.reportError(error);
      }
    }
  }

  private emitState(snapshot: SessionProgressionSnapshot): void {
    for (const listener of [...this.stateListeners]) {
      try {
        listener(snapshot);
      } catch (error) {
        this.reportError(error);
      }
    }
  }

  private reportError(error: unknown): void {
    try {
      this.onError?.(error);
    } catch {
      // Feedback failures never alter deterministic progression state.
    }
  }
}

export function createSessionProgressionSystem(
  options: SessionProgressionOptions = {},
): SessionProgressionSystem {
  return new SessionProgressionSystem(options);
}

function normalizeConfiguration(options: SessionProgressionOptions): SessionProgressionConfiguration {
  const initialCoins = normalizeNonNegativeInteger(
    options.initialCoins ?? DEFAULT_INITIAL_COINS,
    'initialCoins',
  );
  const frontCoinWinAward = normalizeNonNegativeInteger(
    options.frontCoinWinAward ?? DEFAULT_FRONT_COIN_WIN_AWARD,
    'frontCoinWinAward',
  );
  const coinJackpotContribution = normalizeNonNegativeInteger(
    options.coinJackpotContribution ?? DEFAULT_COIN_JACKPOT_CONTRIBUTION,
    'coinJackpotContribution',
  );
  const jackpotThreshold = normalizePositiveInteger(
    options.jackpotThreshold ?? DEFAULT_JACKPOT_THRESHOLD,
    'jackpotThreshold',
  );
  const jackpotAward = normalizeNonNegativeInteger(
    options.jackpotAward ?? DEFAULT_JACKPOT_AWARD,
    'jackpotAward',
  );
  const runOutGraceSeconds = normalizeSeconds(
    options.runOutGraceSeconds ?? DEFAULT_RUN_OUT_GRACE_SECONDS,
    'runOutGraceSeconds',
  );
  return Object.freeze({
    initialCoins,
    insertionCost: 1 as const,
    frontCoinWinAward,
    coinJackpotContribution,
    jackpotThreshold,
    jackpotAward,
    jackpotOverflowPolicy: options.jackpotOverflowPolicy ?? 'carry',
    runOutGraceSeconds,
  });
}

function normalizeId(value: string, label: string): string {
  const id = value.trim();
  if (id.length === 0) throw new TypeError(`${label} must not be empty.`);
  return id;
}

function normalizeSeconds(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new TypeError(`${label} must be a finite non-negative number.`);
  }
  return value;
}

function normalizeNonNegativeInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${label} must be a non-negative safe integer.`);
  }
  return value;
}

function normalizePositiveInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new TypeError(`${label} must be a positive safe integer.`);
  }
  return value;
}

function safeIncrement(value: number, label: string): number {
  return safeAdd(value, 1, label);
}

function safeAdd(left: number, right: number, label: string): number {
  const result = left + right;
  if (!Number.isSafeInteger(result) || result < 0) {
    throw new RangeError(`${label} exceeded the non-negative safe integer range.`);
  }
  return result;
}
